import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/view/components/ui/form'
import useBannerForm, { BannerFormValues } from '../useBannerForm'
import { Separator } from '@/view/components/ui/separator'
import { Input } from '@/view/components/ui/input'
import { Banner, BannerType } from '@/app/entities/Banner'
import { cn } from '@/app/utils/cn'
import { useBannersMutations } from '@/app/hooks/useBannersMutations'
import { getBigCardSize } from '../utils'
import { BannerResponse } from '@/app/services/bannerService'
import { useBanners } from '@/app/hooks/useBanners'
import { Textarea } from '@/view/components/ui/textarea'
import { Label } from '@/view/components/ui/label'
import { Switch } from '@/view/components/ui/switch'
import { fetchImageAsFile } from '@/app/utils/fetchImageAsFile'
import { FormFieldImage } from './components/FormFieldImage'
import { ConfirmButton } from './components/ConfirmButton'
import { FormFieldCheckboxs } from './components/FormFieldCheckboxs'
import { FormFieldLink } from './components/FormFieldLink'
import { useMemo } from 'react'

const theme = import.meta.env.VITE_THEME
const logoUrl = JSON.parse(theme).logo_url

interface BannerFormProps {
  defaultValues?: BannerFormValues
  type: BannerType
  bannerIndex?: number
  onSuccess: () => void
}

const uploadBannerData = (values: BannerFormValues) => ({
  title: values.title ?? '',
  description: values.description ?? '',
  videoUrl: values.isYoutubeVideo ? values.videoUrl : '',
  buttonLabel: values.buttonLabel ?? '',
  link: values.link ?? '',
  file: values.file as unknown as File,
  isTeamsApp: !!values.isTeamsApp,
  isSellersApp: !!values.isSellersApp,
  isWebApp: !!values.isWebApp,
})

const updateCacheIndex = (
  cache: BannerResponse,
  bannerIndex: number,
  newBanner: Banner,
) => {
  const newBanners = [...cache.banners]
  newBanners[bannerIndex] = newBanner
  return newBanners
}

const getImageUrl = (fileUrl: string) => {
  return import.meta.env.VITE_CDN_URL + fileUrl
}

export const BannerForm = ({
  defaultValues,
  type,
  bannerIndex,
  onSuccess,
}: BannerFormProps) => {
  const isEdit = !!defaultValues
  const isDashboardCard = type === BannerType.DASHBOARD_CARD
  const isFixedIndex = isDashboardCard && bannerIndex === 0
  const isSellersCard = isDashboardCard && bannerIndex === 0
  const isInviteBanner = type === BannerType.INVITE

  const form = useBannerForm({ defaultValues })
  const { width, height } = getBigCardSize(type)

  const isYoutubeVideo = form.watch('isYoutubeVideo')

  const imageUrl = defaultValues?.fileUrl
    ? getImageUrl(defaultValues.fileUrl)
    : null

  const { banners: getBanners } = useBanners(type)

  const {
    createBanner,
    updateBanners,
    isUploading,
    isUpdating,
    uploadBannerImage,
    isUploadingImage,
  } = useBannersMutations(type)

  const onSubmit = async (data: BannerFormValues) => {
    if (isDashboardCard && bannerIndex === 0) {
      data.isSellersApp = bannerIndex === 0
    }

    const payload = {
      ...data,
      link: data.link,
      uri: data.fileUrl,
      isTeamsApp: data.isTeamsApp,
      isSellersApp: data.isSellersApp,
      isWebApp: data.isWebApp,
    }

    if (isInviteBanner) payload.isWebApp = true

    if (!data.file && !data.fileUrl) {
      payload.file = await fetchImageAsFile(logoUrl, 'logo_url.png')
    }

    if (!isEdit) {
      await createBanner(uploadBannerData(payload))
      onSuccess()
      return
    }

    if (getBanners && bannerIndex !== undefined) {
      if (data.file) {
        const { banners } = await uploadBannerImage(data.file)
        if (!banners) {
          console.error('No banners returned from upload')
          return
        }
        const addedBanner = banners[banners.length - 1]
        payload.uri = addedBanner.uri
        const newBanners = updateCacheIndex(
          getBanners,
          bannerIndex,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          payload as any,
        )
        await updateBanners(newBanners)
        onSuccess()
        return
      }

      if (!data.fileUrl) return

      const newBanners = updateCacheIndex(
        getBanners,
        bannerIndex,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        payload as any,
      )
      await updateBanners(newBanners)
      onSuccess()
    }
  }

  const bannerText = useMemo(() => {
    if (isEdit) {
      if (isSellersCard) return 'Editar Banner Vendedores'
      if (isInviteBanner) return 'Editar Banner de Convite'
      return 'Editar Banner'
    }
    return 'Novo Banner'
  }, [isEdit, isInviteBanner, isSellersCard])

  const fieldLinkProps = {
    form,
    target: 'banner',
  }

  const fieldCheckboxProps = {
    form,
    isFixedIndex,
    isSellersCard,
  }

  const fieldImageProps = {
    form,
    type,
    height,
    width,
    imageUrl,
  }

  const confirmButtonProps = {
    disabled: isUpdating || isUploading || isUploadingImage,
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn(
          'flex w-full flex-col gap-6',
          isDashboardCard && 'max-w-md',
        )}
      >
        <h1 className="text-2xl font-semibold">{bannerText}</h1>
        <Separator />

        <div
          className={cn(
            'flex w-full  flex-col gap-6',
            !isDashboardCard && 'flex-row',
          )}
        >
          <div
            className={cn(
              'flex w-1/2 flex-col gap-6',
              isDashboardCard && 'w-full',
              isInviteBanner && 'w-full',
            )}
          >
            {!(isFixedIndex || isInviteBanner) && (
              <>
                {[BannerType.DASHBOARD_MODAL].includes(type) ? (
                  <>
                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Título</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Insira um título para o modal"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Descrição</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Insira uma descrição para o modal"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="ml-2 space-y-2">
                      <p className="-ml-2 text-lg font-semibold">
                        Botão de ação
                      </p>

                      <FormField
                        control={form.control}
                        name="buttonLabel"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Texto do botão</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Insira uma descrição para o modal"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormFieldLink
                        {...fieldLinkProps}
                        target="botão de ação"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <FormFieldLink {...fieldLinkProps} />
                    <FormFieldCheckboxs {...fieldCheckboxProps} />

                    {isDashboardCard && <FormFieldImage {...fieldImageProps} />}

                    <ConfirmButton {...confirmButtonProps} />
                  </>
                )}
              </>
            )}

            {((isDashboardCard && isFixedIndex) || isInviteBanner) && (
              <>
                <FormFieldImage {...fieldImageProps} />
                <ConfirmButton {...confirmButtonProps} />
              </>
            )}
          </div>

          {!(isDashboardCard || isInviteBanner) && (
            <div className="flex flex-grow flex-col gap-4">
              {[BannerType.DASHBOARD_MODAL].includes(type) ? (
                <>
                  {!isYoutubeVideo && <FormFieldImage {...fieldImageProps} />}

                  {isYoutubeVideo && (
                    <FormField
                      control={form.control}
                      name="videoUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Vídeo</FormLabel>
                          <FormControl>
                            <Input placeholder="Youtube URL" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="isYoutubeVideo"
                    render={({ field }) => (
                      <FormItem className="w-full space-y-0">
                        <FormControl
                          className={cn('w-full rounded-md bg-primary/10', {
                            'bg-gray-100': !field.value,
                          })}
                        >
                          <div className="flex items-center justify-center space-x-2 p-3">
                            <Label htmlFor="yt" className="font-semibold">
                              Utilizar vídeo do Youtube
                            </Label>
                            <Switch
                              id="yt"
                              checked={!!field.value}
                              onCheckedChange={(value) => {
                                field.onChange(value)
                                if (value) {
                                  form.setValue('file', null)
                                } else {
                                  form.setValue('videoUrl', undefined)
                                }
                              }}
                              ref={field.ref}
                            />
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormFieldCheckboxs {...fieldCheckboxProps} />
                </>
              ) : (
                <FormFieldImage {...fieldImageProps} />
              )}
            </div>
          )}
        </div>

        {[BannerType.DASHBOARD_MODAL].includes(type) && (
          <>
            <ConfirmButton {...confirmButtonProps} />
          </>
        )}
      </form>
    </Form>
  )
}
